import { useEffect, useState } from "react";
import { Badge, EmptyState, Panel, PanelHeader, cn, type Tone } from "@/components/ui";
import { formatCompactMoney, formatDateTime } from "@/lib/format";
import type { CashFlowMonth, FinancialTable, ForecastRow, Metric, ModelOutput } from "@/lib/types";
import { useCase } from "@/components/case/case-context";
import { TraceLink } from "@/components/case/trace";
import { EbitdaBridge } from "./ebitda-bridge";

type View = "financials" | "cash-flow" | "models" | "forecast";

const VIEWS: { key: View; label: string }[] = [
  { key: "financials", label: "Financials" },
  { key: "cash-flow", label: "Cash Flow" },
  { key: "models", label: "Risk Models" },
  { key: "forecast", label: "Forecast" },
];

const VIEW_KEYS = new Set<string>(VIEWS.map((entry) => entry.key));

function isView(value: string): value is View {
  return VIEW_KEYS.has(value);
}

export function AnalysisView() {
  const { analysis } = useCase();
  const [view, setView] = useState<View>("financials");

  /*
   * Open questions deep-link here as `…/analysis#cash-flow`. The target panel
   * only exists while its view is selected, so the hash has to select the view
   * before the browser can scroll to it.
   */
  useEffect(() => {
    const applyHash = () => {
      const hash = window.location.hash.slice(1);
      if (!isView(hash)) return;
      setView(hash);
      // Let the panel mount before asking the browser to scroll to it.
      requestAnimationFrame(() => {
        document.getElementById(hash)?.scrollIntoView({ block: "start" });
      });
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
  }, []);

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="flex flex-wrap gap-1" role="tablist" aria-label="Analysis view">
        {VIEWS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            role="tab"
            aria-selected={view === entry.key}
            onClick={() => setView(entry.key)}
            className={cn(
              "rounded-md border px-2.5 py-1 text-xs font-medium transition-colors",
              view === entry.key
                ? "border-accent-line bg-accent-soft text-accent"
                : "border-line bg-surface text-ink-muted hover:bg-surface-sunken hover:text-ink",
            )}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {view === "financials" ? (
        <div className="space-y-4">
          <FinancialsTable table={analysis.financials} />
          <EbitdaBridge />
        </div>
      ) : null}

      {view === "cash-flow" ? (
        <div className="space-y-4">
          <CashFlowTable months={analysis.cashFlow} />
          <CovenantPanel metrics={analysis.covenantHeadroom} />
        </div>
      ) : null}

      {view === "models" ? <ModelsPanel models={analysis.models} /> : null}

      {view === "forecast" ? <ForecastPanel rows={analysis.forecast} /> : null}
    </div>
  );
}

function FinancialsTable({ table }: { table: FinancialTable }) {
  return (
    <Panel id="financials" className="scroll-mt-6 overflow-x-auto">
      <PanelHeader title="Financials" description="Figures link back to the statement they were read from." />
      <table className="w-full min-w-[34rem] text-sm">
        <thead>
          <tr className="border-b border-line">
            <th scope="col" className="eyebrow px-4 py-2 text-left font-semibold">
              Line
            </th>
            {table.periods.map((period) => (
              <th key={period} scope="col" className="eyebrow px-4 py-2 text-right font-semibold">
                {period}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row) => (
            <tr key={row.id} className="border-b border-line last:border-0 hover:bg-surface-sunken">
              <th scope="row" className={cn("px-4 py-2.5 text-left font-normal", row.emphasis && "font-medium")}>
                <TraceLink trace={row.trace} className="text-ink">
                  {row.label}
                </TraceLink>
              </th>
              {table.periods.map((period) => (
                <td
                  key={period}
                  className={cn("numeric px-4 py-2.5 text-right", row.emphasis ? "font-medium text-ink" : "text-ink-muted")}
                >
                  {row.values[period] ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}

function CashFlowTable({ months }: { months: CashFlowMonth[] }) {
  if (months.length === 0) {
    return (
      <Panel id="cash-flow">
        <EmptyState title="No cash-flow data" detail="Bank statements have not been processed for this case." />
      </Panel>
    );
  }

  const peak = Math.max(...months.map((month) => Math.max(month.inflow, month.outflow)), 1);

  return (
    <Panel id="cash-flow" className="scroll-mt-6 overflow-x-auto">
      <PanelHeader title="Cash flow" description="Monthly inflow against outflow, with the closing balance." />
      <table className="w-full min-w-[38rem] text-sm">
        <thead>
          <tr className="border-b border-line">
            <th scope="col" className="eyebrow px-4 py-2 text-left font-semibold">
              Month
            </th>
            <th scope="col" className="eyebrow px-4 py-2 text-right font-semibold">
              Inflow
            </th>
            <th scope="col" className="eyebrow px-4 py-2 text-right font-semibold">
              Outflow
            </th>
            <th scope="col" className="eyebrow px-4 py-2 text-right font-semibold">
              Closing
            </th>
            <th scope="col" className="eyebrow w-40 px-4 py-2 text-left font-semibold">
              Net
            </th>
          </tr>
        </thead>
        <tbody>
          {months.map((month) => {
            const net = month.inflow - month.outflow;
            const width = Math.round((Math.abs(net) / peak) * 100);
            return (
              <tr key={month.month} className="border-b border-line align-top last:border-0">
                <th scope="row" className="px-4 py-2.5 text-left font-normal text-ink">
                  {month.month}
                  {month.note ? (
                    <p className="mt-0.5 text-xs leading-relaxed text-caution">{month.note}</p>
                  ) : null}
                </th>
                <td className="numeric px-4 py-2.5 text-right text-ink-muted">
                  {formatCompactMoney(month.inflow)}
                </td>
                <td className="numeric px-4 py-2.5 text-right text-ink-muted">
                  {formatCompactMoney(month.outflow)}
                </td>
                <td className="numeric px-4 py-2.5 text-right font-medium text-ink">
                  {formatCompactMoney(month.closing)}
                </td>
                <td className="px-4 py-2.5">
                  {/* A bar, not a chart: the question is only "which months went backwards". */}
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-line">
                      <div
                        className={net >= 0 ? "h-full rounded-full bg-positive" : "h-full rounded-full bg-negative"}
                        style={{ width: `${width}%` }}
                      />
                    </div>
                    <span
                      className={cn(
                        "numeric w-14 text-right text-xs",
                        net >= 0 ? "text-positive" : "text-negative",
                      )}
                    >
                      {formatCompactMoney(net)}
                    </span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Panel>
  );
}

function CovenantPanel({ metrics }: { metrics: Metric[] }) {
  if (metrics.length === 0) return null;
  return (
    <Panel>
      <PanelHeader title="Covenant headroom" />
      <dl className="divide-y divide-line text-sm">
        {metrics.map((metric) => {
          const breach = metric.value.toLowerCase().includes("breach");
          return (
            <div key={metric.id} className="flex items-center justify-between gap-4 px-4 py-2.5">
              <dt className="text-ink-muted">{metric.label}</dt>
              <dd className={cn("numeric font-medium", breach ? "text-negative" : "text-ink")}>
                {metric.value}
              </dd>
            </div>
          );
        })}
      </dl>
    </Panel>
  );
}

const TONE_BADGE: Record<ModelOutput["tone"], Tone> = {
  positive: "positive",
  neutral: "neutral",
  negative: "negative",
};

function ModelsPanel({ models }: { models: ModelOutput[] }) {
  return (
    <Panel id="models" className="scroll-mt-6">
      <PanelHeader title="Risk models" description="Model output is an input to the decision, not the decision." />
      <ul className="divide-y divide-line">
        {models.map((model) => (
          <li key={model.id} className="flex flex-wrap items-start justify-between gap-4 px-4 py-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-ink">{model.name}</span>
                <span className="numeric text-xs text-ink-faint">{model.version}</span>
              </div>
              <p className="mt-1 text-xs text-ink-muted">{model.drivers.join(" · ")}</p>
              <p className="mt-1 text-[0.6875rem] text-ink-faint">Ran {formatDateTime(model.ranAt)}</p>
            </div>
            <Badge tone={TONE_BADGE[model.tone]} className="text-sm">
              {model.result}
            </Badge>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function ForecastPanel({ rows }: { rows: ForecastRow[] }) {
  if (rows.length === 0) {
    return (
      <Panel id="forecast">
        <EmptyState
          title="No forecast"
          detail="A forecast needs a complete historical series. Resolve the outstanding evidence first."
        />
      </Panel>
    );
  }

  return (
    <Panel id="forecast" className="scroll-mt-6 overflow-x-auto">
      <PanelHeader title="Forecast" description="Twelve months forward under three scenarios." />
      <table className="w-full min-w-[30rem] text-sm">
        <thead>
          <tr className="border-b border-line">
            <th scope="col" className="eyebrow px-4 py-2 text-left font-semibold">
              Measure
            </th>
            <th scope="col" className="eyebrow px-4 py-2 text-right font-semibold">
              Downside
            </th>
            <th scope="col" className="eyebrow px-4 py-2 text-right font-semibold">
              Base
            </th>
            <th scope="col" className="eyebrow px-4 py-2 text-right font-semibold">
              Upside
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-b border-line last:border-0">
              <th scope="row" className="px-4 py-2.5 text-left font-normal text-ink">
                {row.label}
              </th>
              <td className="numeric px-4 py-2.5 text-right text-negative">{row.downside}</td>
              <td className="numeric px-4 py-2.5 text-right font-medium text-ink">{row.base}</td>
              <td className="numeric px-4 py-2.5 text-right text-positive">{row.upside}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}
